import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers';
import type { ResolvedMediaControlSettings } from '@/types/playout/media-control-settings.types';
import { GET_LIVE_CONTEXT_STATE_QUERY_KEY } from '@/state/queries/player/use-get-live-context-state';

type PatchPayload = {
  mediaControl?: {
    bindings?: Partial<ResolvedMediaControlSettings['bindings']>;
    allowEventOverride?: boolean;
    allowFlowOverride?: boolean;
  };
};

const apiBase = import.meta.env.VITE_API_URL;

async function patchMediaControl<T extends PatchPayload>(
  url: string,
  patch: T,
  token: string,
): Promise<ResolvedMediaControlSettings> {
  return sendJSONRequest({ url, method: METHOD.PATCH, body: patch, token });
}

export function usePatchOrganizationMediaControlSettingsMutation(organizationId?: string) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: PatchPayload) => {
      const token = await getToken();
      return patchMediaControl(`${apiBase}/organizations/${organizationId}/playout/media-control`, patch, token ?? '');
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organization', organizationId] });
    },
  });
}

export function usePatchEventMediaControlSettingsMutation(eventId?: string) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: PatchPayload) => {
      const token = await getToken();
      return patchMediaControl(`${apiBase}/events/${eventId}/settings/playout/media-control`, patch, token ?? '');
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['event-settings', eventId] });
      void queryClient.invalidateQueries({ queryKey: [GET_LIVE_CONTEXT_STATE_QUERY_KEY] });
    },
  });
}

export function usePatchFlowMediaControlSettingsMutation(flowId?: string) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: PatchPayload) => {
      const token = await getToken();
      return patchMediaControl(`${apiBase}/flows/${flowId}/playout/media-control`, patch, token ?? '');
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['flow', flowId] });
      void queryClient.invalidateQueries({ queryKey: [GET_LIVE_CONTEXT_STATE_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: ['flow-media-control', flowId] });
    },
  });
}
